<template>
  <IconButton
    v-if="!isUnavailable"
    :id="`add-to-list-${productId}`"
    :class="{
      'add-to-list-icon': !isMobile && !isImageIndicatorStylingEnabled,
      'add-to-list-mobile': isMobile || isImageIndicatorStylingEnabled,
      overlay: isImageIndicatorStylingEnabled || isOverlay,
    }"
    aria-label="Add to wishlist"
    title="Add to wishlist"
    size="l"
    :disabled="isLoading"
    data-testid="add-to-list-button"
    @click.stop="addToListHandler"
    @keyup.enter="addToListHandler"
  >
    <span v-if="!isLoading" class="heart-outlined-dark-medium"></span>
    <Spinner v-if="isLoading" data-testid="add-to-list-spinner" />
  </IconButton>
  <Toast
    v-if="state.listId.value || error"
    :toast-container-id="props.containerId"
    :placement="toastPlacement"
    :duration="2000"
    data-testid="add-to-list-toast"
  >
    <div v-if="state.listId.value" data-testid="add-to-list-success">
      Added to <a :href="addToWishlistUrl" data-testid="add-to-list-link">{{ state.listName.value }}</a
      >.
    </div>
    <div v-if="error" class="error-message" data-testid="add-to-list-error">{{ error.message }}</div>
  </Toast>
</template>

<script setup>
import { computed, defineAsyncComponent, markRaw } from 'vue';
import Spinner from '@atomic-ui/spinner/Spinner';
import Toast from '@atomic-ui/toast/ToastRenderer';
import IconButton from '~/components/common/IconButton.vue';
import { useAddToList } from '~/modules/add-to-list/composables/useAddToList';
import { AddToList } from '~/modules/add-to-list/constants/consts';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useSlideout } from '~/composables/utils/useSlideout';
import { useProductType } from '~/composables/useProductType';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProduct } from '~/modules/product/composables/useProduct';
import useExperiments from '~/stores/useExperimentsStore';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { getMemberProduct } from '~/utils/product';

const props = defineProps({
  containerId: {
    type: String,
    required: true,
  },
  isOverlay: {
    type: Boolean,
    default: false,
  },
});

const { productId, productData } = useProduct();
const selectedMemberId = useSelectedMemberId();

const { isMobile } = useDeviceType();
const toastPlacement = computed(() => (isMobile.value ? 'bottom' : 'right'));
const { showSlideout } = useSlideout();
const { isUnavailable } = useProductType();
const featureEligibility = useFeatureEligibility();
const { isShopTheSetExpEnabled } = featureEligibility;
const {
  experimentFlags: { isImageIndicatorStylingEnabled },
} = useExperiments();

/**
 * @param { CustomerListResponse } CustomerList
 */
const slideoutOnSuccess = (CustomerList) => {
  showSlideout({
    id: `add-to-list-${productId}`,
    title: 'Add to list',
    component: markRaw(defineAsyncComponent(() => import('~/modules/add-to-list/components/slideoutContent/AddToListInfo.vue'))),
    props: {
      product: isShopTheSetExpEnabled ? getMemberProduct(productData, selectedMemberId.value) : productData,
      customerList: CustomerList,
      productId: isShopTheSetExpEnabled ? selectedMemberId : productId,
      maxLists: 5,
      featureEligibility,
    },
  });
};

const { state, isLoading, addToList, error } = useAddToList(
  isShopTheSetExpEnabled ? selectedMemberId : productId,
  { slideoutOnSuccess },
  featureEligibility,
);

const addToListHandler = () => {
  addToList();
};

const addToWishlistUrl = computed(() => `${AddToList.addToListPageUrl}?listGuid=${state.listId.value}`);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@csgIcons/new/heart-outlined-dark-medium';

@keyframes product-images-cta-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.add-to-list-icon {
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 1;
}

.add-to-list-mobile {
  position: absolute;
  right: rem-calc(16);
  top: rem-calc(16);
  height: auto;
  background: $white-overlay-color;
  padding: rem-calc(4);
  border-radius: 50%;
  z-index: 2;

  animation-name: product-images-cta-fade;
  animation-delay: 0.1s;
  animation-fill-mode: forwards;
  animation-duration: 0.5s;
}
.toast {
  position: absolute;
  @include margin-top-m;

  @include breakpoint(small only) {
    @include margin-bottom-xxxl;
  }
}
a {
  color: $white;
}
</style>
